import type { UrlOptions, InitialView } from './types';
import type { Pose, SettledMode } from '../controls/types';
import type { QualityTier } from '../render/types';
import { DEFAULT_SEED, CAMERA } from '../core/config';
import { ok, err, type Result } from '../core/result';
import type { TourId } from '../tours/types';

const VALID_QUALITIES: readonly QualityTier[] = ['low', 'medium', 'high'];

function isValidQuality(q: string): q is QualityTier {
  return VALID_QUALITIES.includes(q as QualityTier);
}

function parsePose(poseStr: string): Pose | null {
  const parts = poseStr.split(',').map((s) => parseFloat(s.trim()));
  if (parts.length < 3 || parts.some((n) => !isFinite(n))) {
    return null;
  }

  const [x, y, h] = parts as [number, number, number];
  const yawDeg = parts[3] ?? 0;
  const pitchDeg = parts[4] ?? 0;

  return {
    position: [x, h, -y],
    yaw: (yawDeg * Math.PI) / 180,
    pitch: (pitchDeg * Math.PI) / 180,
    roll: 0,
    fovYDeg: CAMERA.defaultFovYDeg,
  };
}

export function parseUrlOptions(search: string): Result<UrlOptions, string> {
  const params = new URLSearchParams(search);

  const seedStr = params.get('seed');
  const seed = seedStr ? parseInt(seedStr, 10) : DEFAULT_SEED;
  if (!isFinite(seed)) {
    return err('Invalid seed parameter');
  }

  const qualityStr = params.get('quality') ?? 'medium';
  if (!isValidQuality(qualityStr)) {
    return err(`Invalid quality: ${qualityStr}. Valid values: ${VALID_QUALITIES.join(', ')}`);
  }
  const quality = qualityStr;

  const viewStr = params.get('view');
  const poseStr = params.get('pose');

  let view: InitialView;
  if (poseStr) {
    const pose = parsePose(poseStr);
    if (!pose) {
      return err('Invalid pose parameter. Expected: x,y,h,yawDeg,pitchDeg');
    }
    view = { kind: 'pose', pose };
  } else if (viewStr === 'spawn') {
    view = { kind: 'spawn' };
  } else {
    view = { kind: 'painter' };
  }

  const modeStr = params.get('mode');
  const defaultMode = view.kind === 'spawn' ? 'walk' : 'fly';
  const mode: SettledMode =
    modeStr === 'walk' || modeStr === 'fly' || modeStr === 'boat' ? modeStr : defaultMode;

  const still = params.get('still') === '1';
  const automation = params.get('automation') === '1';
  const tour = params.get('tour');
  const timeStr = params.get('time');
  if (timeStr !== null && !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(timeStr))
    return err('Invalid time. Expected HH:MM.');
  const time =
    timeStr === null ? undefined : Number(timeStr.slice(0, 2)) * 60 + Number(timeStr.slice(3));
  if (tour !== null && !['flyover', 'walk', 'boat', 'churches'].includes(tour)) {
    return err('Invalid tour. Choose flyover, walk, boat, or churches.');
  }

  return ok({
    seed,
    quality,
    view,
    mode,
    still,
    automation,
    ...(tour === null ? {} : { tour: tour as TourId }),
    ...(time === undefined ? {} : { time }),
  });
}
